import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { createSerialLoader } from '@/lib/instructionPolling';
import { hasManualTradingOrigin, resolvedManualTradingOrigin } from '@/config/api';
import {
  cancelInstruction,
  getGlobalOpenPause,
  isManualTradingConfigured,
  listInstructions,
  listInstructionsIfIdle,
  pauseGlobalOpen,
  pauseInstruction,
  resumeGlobalOpen,
  resumeInstruction,
  type InstructionView,
} from '@/services/manualTradingApi';
import {
  formatInstructionAmount,
  formatInstructionNotionalProgress,
  formatInstructionSizeProgress,
  instructionNotionalProgressTitle,
  instructionSizeProgressTitle,
  isInstructionProgressNa,
} from '@/lib/instructionDisplay';
import '@/components/ManualTradingPanel/ManualTradingPanel.css';
import './Instructions.css';

const POLL_MS = 10_000;

type ActionFilter = 'all' | 'Open' | 'Close';

function shortExchangeLabel(exchange: string): string {
  const map: Record<string, string> = {
    OKX: 'OKX',
    Bybit: 'BB',
    Binance: 'BN',
    Gateio: 'GT',
    Bitget: 'BG',
    Hyperliquid: 'HL',
    Lighter: 'LT',
    Aster: 'AS',
    EdgeX: 'EX',
    Paradex: 'PD',
  };
  return map[exchange] ?? exchange.slice(0, 4);
}

function formatThresholdMode(mode: string): string {
  switch (mode) {
    case 'Immediate':
      return '立即';
    case 'GreaterOrEqual':
      return '价差≥阈值';
    case 'LessOrEqual':
      return '价差≤阈值';
    default:
      return mode;
  }
}

function formatInstructionValidity(validity: string): string {
  return validity === 'persistent' ? '永久' : '单次';
}

function formatInstructionStatus(status: string): string {
  switch (status) {
    case 'Running':
      return '运行中';
    case 'Paused':
      return '已暂停';
    case 'Completed':
      return '已完成';
    case 'Cancelled':
      return '已取消';
    default:
      return status;
  }
}

function InstructionLegDirection({ row }: { row: InstructionView }) {
  return (
    <span className="trading-engine-leg-dir">
      <span
        className="trading-engine-leg is-long"
        title={`Long ${row.long_exchange} · ${row.long_inst_id}`}
      >
        L&nbsp;{shortExchangeLabel(row.long_exchange)}
      </span>
      <span
        className="trading-engine-leg is-short"
        title={`Short ${row.short_exchange} · ${row.short_inst_id}`}
      >
        S&nbsp;{shortExchangeLabel(row.short_exchange)}
      </span>
    </span>
  );
}

function truncateMiddle(s: string, max: number): string {
  if (s.length <= max) return s;
  const keep = max - 1;
  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${s.slice(0, head)}…${s.slice(s.length - tail)}`;
}

export function Instructions() {
  const { user, isTradingSessionReady } = useAuth();
  const [rows, setRows] = useState<InstructionView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [pausingId, setPausingId] = useState<string | null>(null);
  const [resumingId, setResumingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [globalOpenPaused, setGlobalOpenPaused] = useState(false);
  const [globalOpenPauseLoading, setGlobalOpenPauseLoading] = useState(false);
  const [globalOpenPauseBusy, setGlobalOpenPauseBusy] = useState(false);
  const mounted = useRef(true);

  const loadGlobalOpenPause = useCallback(async () => {
    if (!isManualTradingConfigured() || !isTradingSessionReady || !user) return;
    setGlobalOpenPauseLoading(true);
    try {
      const status = await getGlobalOpenPause();
      if (!mounted.current) return;
      setGlobalOpenPaused(status.paused);
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setGlobalOpenPauseLoading(false);
    }
  }, [isTradingSessionReady, user]);

  const load = useCallback(async (force = false) => {
    if (!isManualTradingConfigured() || !isTradingSessionReady || !user) return;
    const params = {
      action: actionFilter === 'all' ? undefined : actionFilter,
    };
    if (!force) {
      try {
        const data = await listInstructionsIfIdle(params);
        if (data === null || !mounted.current) return;
        setRows(data);
        setError(null);
        setLastOkAt(Date.now());
      } catch (e) {
        if (!mounted.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setRows([]);
      }
      return;
    }
    setLoading(true);
    try {
      const data = await listInstructions(params);
      if (!mounted.current) return;
      setRows(data);
      setError(null);
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [actionFilter, isTradingSessionReady, user]);

  useEffect(() => {
    mounted.current = true;
    if (!isManualTradingConfigured() || !isTradingSessionReady || !user) return undefined;

    void loadGlobalOpenPause();

    const { run: scheduleLoad } = createSerialLoader(async () => {
      await load();
    });

    scheduleLoad();
    const timer = autoRefresh ? window.setInterval(() => scheduleLoad(), POLL_MS) : null;
    return () => {
      mounted.current = false;
      if (timer != null) window.clearInterval(timer);
    };
  }, [autoRefresh, load, isTradingSessionReady, loadGlobalOpenPause, user]);

  const symbolOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.symbol))).sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  useEffect(() => {
    if (!symbolFilter || symbolOptions.includes(symbolFilter)) return;
    setSymbolFilter('');
  }, [symbolFilter, symbolOptions]);

  const filteredRows = useMemo(() => {
    if (!symbolFilter) return rows;
    return rows.filter((row) => row.symbol === symbolFilter);
  }, [rows, symbolFilter]);

  const onCancel = useCallback(
    async (instructionId: string) => {
      if (!window.confirm(`确认取消指令 ${truncateMiddle(instructionId, 16)}？`)) return;
      setCancellingId(instructionId);
      setMessage(null);
      try {
        await cancelInstruction(instructionId);
        setMessage(`已取消指令 ${truncateMiddle(instructionId, 16)}`);
        await load(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setCancellingId(null);
      }
    },
    [load],
  );

  const onPause = useCallback(
    async (instructionId: string) => {
      setPausingId(instructionId);
      setMessage(null);
      setError(null);
      try {
        await pauseInstruction(instructionId);
        setMessage(`已暂停指令 ${truncateMiddle(instructionId, 16)}`);
        await load(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setPausingId(null);
      }
    },
    [load],
  );

  const onResume = useCallback(
    async (instructionId: string) => {
      setResumingId(instructionId);
      setMessage(null);
      setError(null);
      try {
        await resumeInstruction(instructionId);
        setMessage(`已恢复指令 ${truncateMiddle(instructionId, 16)}`);
        await load(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setResumingId(null);
      }
    },
    [load],
  );

  const onToggleGlobalOpenPause = useCallback(async () => {
    setGlobalOpenPauseBusy(true);
    setMessage(null);
    setError(null);
    try {
      const status = globalOpenPaused ? await resumeGlobalOpen() : await pauseGlobalOpen();
      setGlobalOpenPaused(status.paused);
      setMessage(status.paused ? '已暂停对冲套利开仓（指令状态不变）' : '已恢复对冲套利开仓');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGlobalOpenPauseBusy(false);
    }
  }, [globalOpenPaused]);

  if (!hasManualTradingOrigin()) {
    return (
      <div className="instructions-page">
        <header className="instructions-header">
          <h2>套利指令</h2>
          <p className="instructions-desc">
            未配置 trading-engine API（<code>VITE_MANUAL_TRADING_ORIGIN</code>）。请在环境变量中设置后刷新。
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="instructions-page">
      <header className="instructions-header">
        <h2>套利指令</h2>
        <p className="instructions-desc">
          展示当前用户在 trading-engine 中的活跃开仓/关仓指令（Running / Paused）。数据源：
          <code>{resolvedManualTradingOrigin()}</code>
        </p>
      </header>

      <div className="instructions-toolbar">
        <label className="instructions-filter-field">
          Symbol
          <select
            value={symbolFilter}
            disabled={loading}
            onChange={(e) => setSymbolFilter(e.target.value)}
          >
            <option value="">全部</option>
            {symbolOptions.map((sym) => (
              <option key={sym} value={sym}>
                {sym}
              </option>
            ))}
          </select>
        </label>
        <label className="instructions-filter-field">
          方向
          <select
            value={actionFilter}
            disabled={loading}
            onChange={(e) => setActionFilter(e.target.value as ActionFilter)}
          >
            <option value="all">全部</option>
            <option value="Open">开仓</option>
            <option value="Close">关仓</option>
          </select>
        </label>
        <label className="instructions-filter-checkbox">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          自动刷新（{POLL_MS / 1000}s）
        </label>
        <button type="button" disabled={loading} onClick={() => void load(true)}>
          {loading ? '刷新中…' : '刷新'}
        </button>
        <div className="instructions-global-open-control">
          <span
            className={`instructions-global-open-status${globalOpenPaused ? ' is-paused' : ''}`}
          >
            对冲开仓：{globalOpenPauseLoading ? '…' : globalOpenPaused ? '已暂停' : '运行中'}
          </span>
          <button
            type="button"
            className={globalOpenPaused ? 'btn-resume' : 'btn-secondary'}
            disabled={globalOpenPauseBusy || globalOpenPauseLoading || loading}
            onClick={() => void onToggleGlobalOpenPause()}
          >
            {globalOpenPauseBusy
              ? '处理中…'
              : globalOpenPaused
                ? '恢复对冲开仓'
                : '暂停对冲开仓'}
          </button>
        </div>
      </div>

      {globalOpenPaused ? (
        <p className="instructions-global-open-banner">
          对冲套利开仓已暂停：Running 开仓指令不会触发对冲下单，指令状态保持不变；关仓与单腿下单不受影响。
        </p>
      ) : null}

      <p className="instructions-meta">
        共 {filteredRows.length} 条
        {symbolFilter ? `（Symbol = ${symbolFilter}）` : ''}
        {actionFilter !== 'all' ? ` · ${actionFilter === 'Open' ? '开仓' : '关仓'}` : ''}
        {lastOkAt ? ` · 更新于 ${new Date(lastOkAt).toLocaleTimeString('zh-CN', { hour12: false })}` : ''}
      </p>

      {error ? <p className="instructions-error">{error}</p> : null}
      {message ? <p className="instructions-ok">{message}</p> : null}

      <div className="instructions-table-wrap">
        {filteredRows.length === 0 && !loading && !error ? (
          <p className="instructions-empty">暂无活跃指令。</p>
        ) : (
          <table className="instructions-table trading-engine-orders-table trading-engine-instructions-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>方向</th>
                <th>类型</th>
                <th>名义 (USD)</th>
                <th>数量 (size)</th>
                <th>单次名义</th>
                <th>阈值</th>
                <th>间隔 (ms)</th>
                <th>状态</th>
                <th>指令 ID</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.instruction_id}>
                  <td className="instructions-symbol">{row.symbol}</td>
                  <td>
                    <InstructionLegDirection row={row} />
                  </td>
                  <td>
                    {row.action === 'Close'
                      ? `关仓 · ${formatInstructionValidity(row.validity)} · ${formatThresholdMode(row.threshold_mode)}`
                      : `开仓 · ${formatThresholdMode(row.threshold_mode)}`}
                  </td>
                  <td
                    className={`num${isInstructionProgressNa(formatInstructionNotionalProgress(row)) ? ' instruction-progress-na' : ''}`}
                    title={instructionNotionalProgressTitle(row)}
                  >
                    {formatInstructionNotionalProgress(row)}
                  </td>
                  <td
                    className={`num${isInstructionProgressNa(formatInstructionSizeProgress(row)) ? ' instruction-progress-na' : ''}`}
                    title={instructionSizeProgressTitle(row)}
                  >
                    {formatInstructionSizeProgress(row)}
                  </td>
                  <td className="num">{formatInstructionAmount(row.per_order_notional)}</td>
                  <td className="num">{row.threshold ?? '—'}</td>
                  <td className="num">{row.min_order_interval_ms}</td>
                  <td>
                    <span className={`instructions-status is-${row.status.toLowerCase()}`}>
                      {formatInstructionStatus(row.status)}
                    </span>
                  </td>
                  <td className="instructions-id" title={row.instruction_id}>
                    {truncateMiddle(row.instruction_id, 14)}
                  </td>
                  <td>
                    <div className="instructions-actions">
                      {row.status === 'Running' ? (
                        <button
                          type="button"
                          className="btn-secondary btn-inline"
                          disabled={
                            pausingId === row.instruction_id ||
                            resumingId === row.instruction_id ||
                            cancellingId === row.instruction_id
                          }
                          onClick={() => void onPause(row.instruction_id)}
                        >
                          {pausingId === row.instruction_id ? '暂停中…' : '暂停'}
                        </button>
                      ) : null}
                      {row.status === 'Paused' ? (
                        <button
                          type="button"
                          className="btn-resume btn-inline"
                          disabled={
                            pausingId === row.instruction_id ||
                            resumingId === row.instruction_id ||
                            cancellingId === row.instruction_id
                          }
                          onClick={() => void onResume(row.instruction_id)}
                        >
                          {resumingId === row.instruction_id ? '恢复中…' : '恢复'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn-danger btn-inline"
                        disabled={
                          pausingId === row.instruction_id ||
                          resumingId === row.instruction_id ||
                          cancellingId === row.instruction_id
                        }
                        onClick={() => void onCancel(row.instruction_id)}
                      >
                        {cancellingId === row.instruction_id ? '取消中…' : '取消'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
